import { useEffectOnce } from 'react-use';
import { Trans } from 'react-i18next';
import type { CSSProperties } from 'react';

import { Icon, Stack, Badge, Tooltip, Link, Text, tokens, Button } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { useErrorHandler } from '@hotjar/error-handler';
import { getSalesLink, tracking } from '@hotjar/upsell';
import { PRICING_PAGE_LINK } from '@hotjar/webapp/features/Billing/constants';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import type { IconSetDefinitions } from '@hotjar/hotsauce/build/components/Icon/icons'; // TODO: we shouldn't import internals of another package
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useReadableProductName } from '@hotjar/webapp/domains/common/product/hooks/useReadableProductName';
import type { SubmitCheckoutParams } from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';
import { SUBSCRIPTIONS } from '@hotjar/subscription-management/src/constants/routes'; // TODO: we shouldn't import internals of another package
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { useIdentifiedUsersCustomEvents } from '@hotjar/webapp/hooks/useIdentifiedUsersCustomEvents';
import { useCurrentUser } from '@hotjar/user';
import { useResponsesLimit } from '@hotjar/feedback';
import { useGetSiteProducts } from '@hotjar/webapp/domains/common/product/hooks/useGetSiteProducts';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';

import { logError } from '@hotjar/insights-common/lib/logger';
import { upsellLogger } from '@hotjar/webapp/domains/revenue/utils';
import {
  DatadogUpsellType,
  DatadogActionType,
} from '@hotjar/webapp/domains/revenue/upsell/constants';
import { useSubscriptionInfo } from '@hotjar/subscription-management';
import { ModalStyles as S } from './ProductSelectUpsellModal.styles';
import { CheckboxItem } from './components/CheckboxItem';
import { ProductSelectActions } from './components/ProductSelectActions';
import type {
  SelectedProductTypes,
  ChangeSelectedProductTypes,
} from '../MultiProductWrapper/hooks/useMultiProductsState';
import { AskLimitsCard } from './components/AskLimitsCard/AskLimitsCard';
import { ObserveLimitsCard } from './components/ObserveLimitsCard';

interface Props {
  site: Site;
  selectedProductTypes: SelectedProductTypes;
  changeSelectedProductTypes: ChangeSelectedProductTypes;
  goNextStep: () => void;
}

export type CheckboxItemData = {
  id: ProductType;
  title: string;
  subTitle: string;
  icon: keyof IconSetDefinitions;
};

const helpers = [
  {
    i18nKey: 'billing:product-select-upsell-modal.book-a-call-label',
    href: getSalesLink('InAppModal'),
  },
  {
    i18nKey: 'billing:product-select-upsell-modal.pricing-page-label',
    href: PRICING_PAGE_LINK,
  },
  {
    i18nKey: 'billing:product-select-upsell-modal.downgrade-label',
    href: SUBSCRIPTIONS,
  },
];

const HOTJAR_EVENT_UA_DOC_LINK = '/articles/4419626303767';
const CONTACT_SUPPORT_LINK =
  '/requests/new?ticket_form_id=360000390393&insightsapp=&is_verified=false';

const ExternalLink = ({
  href,
  style,
  children,
}: {
  href: string;
  style?: CSSProperties;
  children?: string;
}) => {
  return (
    <Link
      kind="standalone"
      style={{ fontSize: 'inherit', lineHeight: 'inherit', ...(style || {}) }}
      href={href || ''}
      isExternal
    >
      {children as string}
    </Link>
  );
};

const trackingStepName = 'Product selection';

export const ProductSelectUpsellModal = ({
  site,
  onCancel,
  selectedProductTypes,
  changeSelectedProductTypes,
  goNextStep,
}: Props & GlobalModalProps<SubmitCheckoutParams>): JSX.Element => {
  const handleError = useErrorHandler();
  const [t] = useTranslationWithPrefix(['billing:product-select-upsell-modal']);
  const helpHotjarUrl = useHelpHotjarUrl();
  const {
    data: currentUser,
    isLoading: isLoadingCurrentUser,
    isError: isErrorCurrentUser,
  } = useCurrentUser();
  const {
    data: identifiedUserCustomEvents,
    isLoading: isLoadingIdentifiedUserCustomEvents,
    isError: isErrorIdentifiedUserCustomEvents,
  } = useIdentifiedUsersCustomEvents(site);
  const {
    analyticsProduct: currentAnalyticsProduct,
    feedbackProduct: currentFeedbackProduct,
    isLoading: isLoadingSiteProducts,
    isError: isErrorSiteProducts,
  } = useGetSiteProducts(site);
  const {
    coveragePercentage,
    coverageLevel,
    dailyTraffic,
    days: daysMeasured,
    isError: isErrorTrafficCoverage,
    isLoading: isLoadingTrafficCoverage,
  } = useTrafficCoverage(site);

  const {
    responseLimitInfo,
    rolloverDate,
    isError: isResponsesLimitError,
  } = useResponsesLimit(true, site);
  const { getState } = useTrackingCode();
  const state = getState(site.lastData);
  const isTrackingIssue = !state.isActive;
  const lastActiveHoursAgo = state.lastData.hoursSince ?? 0;
  const lastActiveDaysAgo = Math.ceil(lastActiveHoursAgo / 24);

  const { data: subscriptionInfo } = useSubscriptionInfo();
  const isExternalPaymentCustomer =
    subscriptionInfo?.customer?.paymentMethod.paymentMethodType === 'EXTERNAL_PAYMENT_METHOD';

  const isLoading = Boolean(
    isLoadingCurrentUser ||
      isLoadingIdentifiedUserCustomEvents ||
      isLoadingSiteProducts ||
      isLoadingTrafficCoverage,
  );

  const isCriticalError = isErrorCurrentUser;

  const isError =
    isResponsesLimitError ||
    isErrorCurrentUser ||
    isErrorIdentifiedUserCustomEvents ||
    isErrorSiteProducts ||
    isErrorTrafficCoverage;

  const title = isExternalPaymentCustomer ? t('external-payments.title') : t('title');
  const subtitle = isExternalPaymentCustomer
    ? t('external-payments.subtitle')
    : t('subtitle', { siteName: site?.name });

  useEffectOnce(() => {
    /**
     * This timeout here is a workaround that we need because we fire the `upsellFlowEntered` event in
     * `MultiProductWrapper` the same time as we fire `upsellFlowStepStarted` here. Due to these being kind of
     * concurrently rendered, sometimes the `upsellFlowStepStarted` event gets fired first, which doesn't make sense
     * semantically. We want to defer firing it after effects in `MultiProductWrapper` have run, and we do this by
     * wrapping the event here in a timeout that will only put the function call onto the call stack once it has
     * emptied. An alternative could've been to introduce a ref/state variable in `MultiProductWrapper` and pass it
     * down to this component such that the event here only gets fired once the variable says so, but that didn't
     * feel perfect either.
     */
    setTimeout(() => {
      tracking.upsellFlowStepStarted({
        account: site.account,
        stepName: trackingStepName,
      });
    }, 0);
  });

  const onContinueClick = () => {
    tracking.upsellFlowStepFinished({
      account: site.account,
      stepName: trackingStepName,
      productSelected: (Object.keys(selectedProductTypes) as ProductType[]).filter(
        (productType) => selectedProductTypes[productType],
      ),
    });
    goNextStep();
  };

  const products: CheckboxItemData[] = [
    {
      id: ProductType.Analytics,
      title: useReadableProductName(ProductType.Analytics),
      subTitle: t('products.observe.sub-title'),
      icon: 'hidden',
    },
    {
      id: ProductType.Feedback,
      title: useReadableProductName(ProductType.Feedback),
      subTitle: t('products.ask.sub-title'),
      icon: 'question',
    },
  ];

  const onChangeCheckedProductType = (productType: ProductType) => () => {
    changeSelectedProductTypes(productType);
  };

  const { show: openTrackingCodeModal } = useModalTrackingCode();

  const onVerificationClick = () => {
    onCancel();
    openTrackingCodeModal(site, 2, 'Multi-product upsell modal - product selection');
  };

  useEffectOnceWithDependencies(
    () => {
      if (isCriticalError) {
        handleError(new Error(`ProductSelectUpsellModal: Error while fetching data.`));
      }
    },
    [isCriticalError],
    () => isCriticalError,
  );

  useEffectOnceWithDependencies(
    () => {
      const error = new Error(`ProductSelectUpsellModal: Non critical error while fetching data.`);

      logError(error);

      upsellLogger?.error('Multi-product modal', {
        upsellType: DatadogUpsellType.MultiProductWrapper,
        action: DatadogActionType.Failure,
        errorMessage: error.message,
      });
    },
    [isError, isCriticalError],
    () => isError && !isCriticalError,
  );

  return (
    <S.Modal isOpen contentLabel={title} onClose={onCancel} data-testid="product-select-modal">
      <S.Loader isLoading={isLoading} size="xl">
        <S.ModalHeader title={title} subtitle={subtitle} />
        <S.ModalBody>
          <S.ProductSelectWraper>
            <Stack direction="row" spacing="x3">
              {products.map((product) => (
                <Tooltip
                  text={`${t('external-payments.tooltip')}`}
                  isDisabled={!isExternalPaymentCustomer}
                >
                  <S.Product>
                    <CheckboxItem
                      key={product.id}
                      isDisabled={isExternalPaymentCustomer}
                      isChecked={!isExternalPaymentCustomer && selectedProductTypes[product.id]}
                      setIsChecked={onChangeCheckedProductType(product.id)}
                      data={product}
                    />
                    {product.id === ProductType.Analytics && (
                      <ObserveLimitsCard
                        site={site}
                        currentTier={currentAnalyticsProduct?.tier ?? 'basic'}
                        currentPlanAllowance={currentAnalyticsProduct?.dailyCcSessions ?? 0}
                        dailyTraffic={dailyTraffic}
                        coveragePercentage={coveragePercentage}
                        daysMeasured={daysMeasured}
                        coverageLevel={coverageLevel}
                        isMeasuring={daysMeasured === 0}
                        isError={isErrorTrafficCoverage}
                        isLoading={isLoadingTrafficCoverage}
                        isTrackingIssue={isTrackingIssue}
                        lastActiveDaysAgo={lastActiveDaysAgo}
                        onVerificationClick={onVerificationClick}
                      />
                    )}
                    {product.id === ProductType.Feedback && (
                      <AskLimitsCard
                        currentTier={currentFeedbackProduct?.tier ?? ''}
                        rolloverDate={rolloverDate}
                        responseLimitInfo={responseLimitInfo}
                        isError={isResponsesLimitError}
                        lastActiveDaysAgo={lastActiveDaysAgo}
                        onVerificationClick={onVerificationClick}
                      />
                    )}
                  </S.Product>
                </Tooltip>
              ))}
            </Stack>
            {!isExternalPaymentCustomer && (
              <S.BundleDiscount>
                <Trans
                  i18nKey="billing:product-select-upsell-modal.bundle-discount-label"
                  components={[
                    <Badge
                      kind="custom"
                      size="m"
                      textColor={tokens.color.text_success}
                      color={tokens.color.bg_success}
                    />,
                  ]}
                />
                <Tooltip text={t('bundle-discount-tooltip')} hideOnClick={false}>
                  <Icon
                    inline
                    color="text_default"
                    icon="info"
                    size="m"
                    css={{ marginLeft: '5px' }}
                  />
                </Tooltip>
              </S.BundleDiscount>
            )}
          </S.ProductSelectWraper>
          <S.BodyInfo>
            <Text textType="body2" textColor="text_light">
              <Trans
                i18nKey="billing:product-select-upsell-modal.x-users-and-y-custom-events"
                values={{
                  sessionAmount: abbrNumber(identifiedUserCustomEvents?.identifiedSessions ?? 0),
                  eventAmount: abbrNumber(identifiedUserCustomEvents?.customEvents ?? 0),
                }}
                components={[<ExternalLink href={`${helpHotjarUrl}${HOTJAR_EVENT_UA_DOC_LINK}`} />]}
              />
            </Text>
            <S.Divider />
            <S.ExternalLinkList>
              {helpers.map((helper) => (
                <S.ExternalLinkListItem key={helper.i18nKey}>
                  <Trans
                    i18nKey={helper.i18nKey}
                    components={[
                      <ExternalLink href={helper.href} style={{ marginLeft: tokens.spacer.x1 }} />,
                    ]}
                  />
                </S.ExternalLinkListItem>
              ))}
            </S.ExternalLinkList>
          </S.BodyInfo>
        </S.ModalBody>

        <S.ModalFooter>
          {isExternalPaymentCustomer ? (
            <Button type="button" kind="primary" as="a" isExternal href={CONTACT_SUPPORT_LINK}>
              {t('external-payments.cta-contact-support')}
            </Button>
          ) : (
            <ProductSelectActions
              selectedProductTypes={selectedProductTypes}
              onContinueClick={onContinueClick}
              currentSite={site}
              currentUser={currentUser}
            />
          )}
        </S.ModalFooter>
      </S.Loader>
    </S.Modal>
  );
};
